/**
 * Crops an image based on the specified crop area and returns it as a File object.
 * 
 * - Creates an image from the provided image source
 * - Draws the cropped portion of the image onto a canvas.
 * - Returns a `File` object of the cropped image with the specified filename.
 * 
 * @param {string | File} imageSrc - Image source File to crop.
 * @param {Object} crop - Crop area dimensions (x, y, width, height).
 * @param {string} [filename="cropped-image.jpg"] - Optional filename for the cropped image.
 * @returns {Promise<File>} - A promise that resolves to the cropped image as a File object.
 */
export async function getCroppedImg(imageSrc, crop, filename = "cropped-image.jpg") {
    const image = await createImage(imageSrc);

    // Create a new canvas to draw the cropped area
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Set the canvas dimensions to match the cropped area
    canvas.width = crop.width;
    canvas.height = crop.height;

    // Draw the cropped portion of the image onto the canvas
    ctx.drawImage(
        image, 
        crop.x, crop.y,
        crop.width, crop.height,
        0, 0,
        crop.width, crop.height 
    );

    // Convert the canvas to a blob (image data) and return it as a File object
    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            const file = new File([blob], filename, { type: "image/jpeg" });
            resolve(file);
        }, "image/jpeg", 0.85); 
    });
}

/**
 * Creates an HTML image element from the provided image URL or File.
 * 
 * - Loads the image asynchronously and returns a Promise that resolves to the image element.
 * - Supports cross-origin image loading (via `crossOrigin = "anonymous"`).
 * 
 * @param {string} url - The URL or File object for the image source.
 * @returns {Promise<Image>} - A promise that resolves to an HTMLImageElement once the image is loaded.
 */
export const createImage = (url) => {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = "anonymous"; // Ensure cross-origin support for the image
        image.src = url;
        image.onload = () => resolve(image);
        image.onerror = reject;
    });
};


/**
 * Computes the aspect ratio for the cropping operation based on the selected orientation.
 * 
 * - Adjusts the aspect ratio depending on whether the orientation is "landscape" or "portrait".
 * - If the orientation is "landscape" and the base aspect ratio is taller than it is wide, it rotates the aspect ratio.
 * - If the orientation is "portrait" and the base aspect ratio is wider than it is tall, it rotates the aspect ratio.
 * 
 * @param {Object} baseAspect - The base aspect ratio (width and height) for the crop.
 * @param {"landscape" | "portrait"} orientation - The selected orientation for cropping.
 * @returns {number} - The computed aspect ratio for the crop operation.
 */
export const getComputedAspect = (baseAspect, orientation) => {
    const ratio = baseAspect.width / baseAspect.height;

    // Landscape: If width < height, rotate the aspect ratio
    if (orientation === "landscape" && baseAspect.width < baseAspect.height) {
        return baseAspect.height / baseAspect.width; 
    }

    // Portrait: If height < width, rotate the aspect ratio
    if (orientation === "portrait" && baseAspect.height < baseAspect.width) {
        return baseAspect.height / baseAspect.width; 
    }

    return ratio;
};
